import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import {
  Button,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  Pagination,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "Components/ui";
import { FilePlus2, ReceiptText, RefreshCw } from "Components/ui/icons";
import { InvoiceListItem, InvoiceListQuery, InvoiceStatus } from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import { cn } from "utils/cn";
import { formatDate, formatINR, todayIST } from "utils/format";
import { InvoiceStatusChip } from "Routes/Billing/Components/StatusChips";
import { useInvoiceList } from "../data";
import { BillingPermissions } from "../types";

type InvoiceFilter = "all" | "open" | "draft" | "paid" | "void" | "review";

const FILTERS: Array<{
  id: InvoiceFilter;
  label: string;
  status?: InvoiceStatus[];
  review?: boolean;
}> = [
  { id: "all", label: "All" },
  { id: "open", label: "Open", status: ["issued", "partially_paid"] },
  { id: "draft", label: "Drafts", status: ["draft"] },
  { id: "paid", label: "Paid", status: ["paid"] },
  { id: "void", label: "Void", status: ["void"] },
  { id: "review", label: "Needs review", review: true },
];

const PAGE_SIZE = 20;

const isOverdue = (invoice: InvoiceListItem, today: string) =>
  (invoice.status === "issued" || invoice.status === "partially_paid") &&
  invoice.balance > 0 &&
  invoice.dueDate < today;

const balanceClass = (invoice: InvoiceListItem) => {
  if (invoice.balance < 0) {
    return "text-warning-text";
  }
  return invoice.balance > 0 && invoice.status !== "void"
    ? "text-foreground"
    : "text-subtle-foreground";
};

interface InvoicesTabProps {
  tenantId: string;
  permissions: BillingPermissions;
  onOpenInvoice: (invoiceId: string) => void;
  onAddInvoice: () => void;
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({
  tenantId,
  permissions,
  onOpenInvoice,
  onAddInvoice,
}) => {
  const [filter, setFilter] = React.useState<InvoiceFilter>("all");
  const [page, setPage] = React.useState<number>(1);
  const option = FILTERS.find((candidate) => candidate.id === filter) || FILTERS[0];
  const query: InvoiceListQuery = {
    tenantId,
    status: option.status,
    needsReview: option.review,
    page,
    pageSize: PAGE_SIZE,
  };
  const invoices = useInvoiceList(query);
  const rows = invoices.data ? invoices.data.data : [];
  const total = invoices.data ? invoices.data.total : 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Step back when the current page empties (e.g. after a void or reversal).
  React.useEffect(() => {
    if (invoices.data && page > lastPage) {
      setPage(lastPage);
    }
  }, [invoices.data, page, lastPage]);
  const today = todayIST();

  const columns = React.useMemo<ColumnDef<InvoiceListItem>[]>(
    () => [
      {
        id: "invoice",
        header: "Invoice",
        cell: ({ row }) => {
          const invoice = row.original;
          return (
            <div className="py-2">
              <p
                className={cn(
                  "text-sm font-semibold text-foreground",
                  invoice.number && "font-mono"
                )}
              >
                {invoice.number || "Draft"}
              </p>
              <p className="text-xs text-muted-foreground">
                {invoice.title || (invoice.source === "manual" ? "One-time" : "Recurring")}
                {invoice.periodIndex !== null ? ` · period ${invoice.periodIndex + 1}` : ""}
              </p>
            </div>
          );
        },
      },
      {
        id: "due",
        header: "Due",
        cell: ({ row }) => (
          <span
            className={cn(
              "whitespace-nowrap",
              isOverdue(row.original, today) ? "font-semibold text-danger-text" : "text-foreground"
            )}
          >
            {formatDate(row.original.dueDate)}
          </span>
        ),
      },
      {
        id: "total",
        header: "Total",
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums text-foreground">
            {formatINR(row.original.total)}
          </span>
        ),
      },
      {
        id: "paid",
        header: "Paid",
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums text-muted-foreground">
            {formatINR(row.original.amountPaid)}
          </span>
        ),
      },
      {
        id: "balance",
        header: "Balance",
        meta: { align: "right" },
        cell: ({ row }) => (
          <span
            className={cn(
              "whitespace-nowrap font-semibold tabular-nums",
              balanceClass(row.original)
            )}
          >
            {formatINR(row.original.balance)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <InvoiceStatusChip
            status={row.original.status}
            needsReview={row.original.needsReview}
            overdue={isOverdue(row.original, today)}
          />
        ),
      },
    ],
    [today]
  );

  const empty = invoices.isError ? (
    <ErrorState
      title="Invoices didn't load"
      description={getApiErrorMessage(invoices.error)}
      action={
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw aria-hidden="true" />}
          onClick={() => invoices.refetch()}
        >
          Retry
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={<ReceiptText aria-hidden="true" />}
      title={filter === "all" ? "No invoices yet" : "No invoices in this view"}
      description={
        filter === "all"
          ? "Recurring charges create invoices automatically before each due date."
          : undefined
      }
    />
  );

  return (
    <section className="space-y-4">
      <CardHeader
        className="p-0"
        title="Invoices"
        description="Newest first. Open one for items, payments, reminders and actions."
        actions={
          permissions.canWrite ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onAddInvoice}
              leftIcon={<FilePlus2 aria-hidden="true" />}
            >
              One-time invoice
            </Button>
          ) : null
        }
      />
      <Tabs
        value={filter}
        onValueChange={(value) => {
          const next = FILTERS.find((candidate) => candidate.id === value);
          if (next) {
            setFilter(next.id);
            setPage(1);
          }
        }}
      >
        <TabsList
          variant="pill"
          aria-label="Filter invoices"
          className="max-w-full overflow-x-auto"
        >
          {FILTERS.map((candidate) => (
            <TabsTrigger key={candidate.id} value={candidate.id}>
              {candidate.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={filter} className="mt-3">
          <DataTable
            columns={columns}
            data={invoices.isError ? [] : rows}
            loading={invoices.isLoading}
            empty={empty}
            enableSorting={false}
            caption="Invoices"
            getRowId={(invoice) => invoice._id}
            onRowClick={(invoice) => onOpenInvoice(invoice._id)}
            className={cn(
              "transition-opacity duration-fast",
              invoices.isFetching && !invoices.isLoading && "opacity-70"
            )}
            footer={
              invoices.data && invoices.data.total > 0 ? (
                <Pagination
                  page={page}
                  totalPages={lastPage}
                  onPageChange={setPage}
                  totalItems={invoices.data.total}
                  pageSize={PAGE_SIZE}
                  noun="invoices"
                  disabled={invoices.isFetching}
                />
              ) : undefined
            }
          />
        </TabsContent>
      </Tabs>
    </section>
  );
};

export default InvoicesTab;
